import { useState } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import {
  Button,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
// components
import TodoHistoryPopoverComponent from './TodoHistoryPopoverComponent';
import SvgIconStyle from '../../components/SvgIconStyle';
import Iconify from '../../components/Iconify';
import Label from '../../components/Label';
// hooks
import useAuth from '../../hooks/useAuth';
// swr
import useSWR from 'swr';
// utils
import axiosSWRFetcher from '../../utils/swrFetcher';
import { fKrDateKST } from '../../utils/formatTime';
// config
import { EVENT_ID } from '../../config';

// ----------------------------------------------------------------------

const ScrollContent = styled('div')(({ theme }) => ({
  maxHeight: 'calc(410px - 90px)',
  overflow: 'auto',
}));

export default function TodoHistoryPopover({ crewId }) {
  const { user } = useAuth();
  const companyId = user?.employee.company[0].id;
  const { data, isValidating } = useSWR((companyId && crewId) ? `/api/works/todo/company/${companyId}/crew/${crewId}/history/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});

  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const historyLabelColor = {
    create: 'info',
    delete: 'error',
    edit: 'oldprimary',
    done: 'primary',
    cancel: 'default',
  }

  const handleClose = () => {
    setOpen(null);
  };

  return (
    <>
      <Button
        disableRipple
        id={EVENT_ID.bizTodoHistory}
        sx={{
          ...(open && {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            '&:before': {
              zIndex: 1,
              content: "''",
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.5),
            },
          }),
        }}
        variant="outlined"
        onClick={handleOpen}
        startIcon={
        <Iconify icon="mdi:clock-time-three-outline" />
      }>
        수정 내역
      </Button>

      <TodoHistoryPopoverComponent
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          '& .MuiMenuItem-root': {
            typography: 'body2',
          },
        }}
      >
        <Stack sx={{ position: 'fixed', width: '100%', zIndex: 10 }}>
          <Stack sx={{ px: 2.5, py: 2 }}>
            <Typography
              variant="subtitle1"
            >
              수정 내역
            </Typography>
          </Stack>
        </Stack>
        <Divider sx={{ pt: 7 }} />
        {!isValidating && data?.history?.length === 0 && (
          <Typography sx={{ textAlign: 'center', py: 4 }} variant="subtitle2">
            수정 내역이 없습니다
          </Typography>
        )}
        {!isValidating && data?.history?.length > 0 && (
        <ScrollContent>
        {!isValidating && data?.history?.map((h, index) => (
          <Stack direction="row" key={`h-${index}`} sx={{ px: 2.5, py: 2, position: 'relative' }} spacing={2} alignItems="initial">
            <Label
              sx={{ minWidth: '38px', ...h.history_type === 'cancel' && {whiteSpace: 'pre-wrap', height: '42px', lineHeight: 1.5} }}
              variant='filled'
              color={historyLabelColor[h.history_type]}
            >
              {
                (h.history_type === 'cancel' && '완료\n취소') ||
                (h.history_type === 'delete' && '삭제') ||
                (h.history_type === 'done' && '완료') ||
                (h.history_type === 'edit' && '수정') ||
                (h.history_type === 'create' && '추가')
              }
            </Label>
            <Stack>
              <Typography
                variant="subtitle2"
                sx={{
                  ...(h.history_type === 'done' || h.history_type === 'delete') && { textDecoration: 'line-through' },
                  ...h.history_type === 'delete' && { color: 'text.secondary' }
                }}
              >
                {h.content}
              </Typography>
              <Stack direction="row" sx={{ color: 'text.disabled' }} spacing={0.5} alignItems="center">
                <Iconify icon="mdi:clock-time-three-outline" />
                <Typography variant="caption">
                  {fKrDateKST(h.created_at)}
                </Typography>
              </Stack>
              {h.changed_crew && (
                <Stack direction="row" sx={{ color: 'text.disabled' }} spacing={0.5} alignItems="center">
                  <SvgIconStyle src={`/icons/ic_person.svg`} sx={{ color: "text.disabled", width: 16, height: 16 }} />
                  <Typography variant="caption">
                    <b>{h.changed_crew.nickname}</b> 크루
                  </Typography>
                </Stack>
              )}
              {h.changed_company && (
                <Stack direction="row" sx={{ color: 'text.disabled' }} spacing={0.5} alignItems="center">
                  <SvgIconStyle src={`/icons/ic_person.svg`} sx={{ color: "text.disabled", width: 16, height: 16 }} />
                  <Typography variant="caption">
                    <b>{h.changed_company.name}</b>
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        ))}
        </ScrollContent>
        )}
        <Divider sx={{ mb: 4 }} />
      </TodoHistoryPopoverComponent>
    </>
  );
}
